import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthCallback from "./pages/AuthCallback";
import Application from "./pages/Application";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import Messages from "./pages/Messages";
import AdminDashboard from "./pages/AdminDashboard";
import Philosophy from "./pages/Philosophy";
import Movement from "./pages/Movement";
import Insights from "./pages/Insights";
import Manifesto from "./pages/Manifesto";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Onboarding from "./pages/Onboarding";
import GetStarted from "./pages/GetStarted";
import ProfileBuilder from "./pages/ProfileBuilder";
import Discover from "./pages/Discover";
import Stories from "./pages/Stories";

import "./App.css";

// Protected Route Component
const ProtectedRoute = ({ children, requireApproved = false, requireAdmin = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <div className="text-charcoal font-mono text-sm uppercase tracking-widest">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (requireAdmin && !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireApproved) {
    if (user.application_status !== "approved") {
      return <Navigate to="/application" replace />;
    }
    if (user.payment_status !== "completed") {
      return <Navigate to="/application" state={{ step: "payment" }} replace />;
    }
  }

  return children;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();

  // CRITICAL: Check for session_id SYNCHRONOUSLY during render
  // This prevents race conditions with auth checks
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/get-started" element={<GetStarted />} />
      <Route path="/profile-builder" element={<ProfileBuilder />} />
      <Route path="/discover" element={<Discover />} />
      <Route path="/stories" element={<Stories />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/philosophy" element={<Philosophy />} />
      <Route path="/movement" element={<Movement />} />
      <Route path="/insights" element={<Insights />} />
      <Route path="/manifesto" element={<Manifesto />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/application"
        element={
          <ProtectedRoute>
            <Application />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requireApproved>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/:userId"
        element={
          <ProtectedRoute requireApproved>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute requireApproved>
            <Messages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages/:userId"
        element={
          <ProtectedRoute requireApproved>
            <Messages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="bottom-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
